import { useEffect, useRef, useState } from 'react';

/**
 * The two cards before the game: the studio, then ShweTap.
 *
 * ## The cost is real, so it is spent on something
 *
 * A splash is time added to every cold start, and `docs/LOADING_SCREEN.md` is
 * explicit that this app does not pad waits. Two things keep that honest:
 *
 * 1. **The cards run while `init()` is in flight.** They are not shown *before*
 *    booting starts; the network request and the animation overlap, so on a slow
 *    connection the splash is free — it is covering a wait that was happening anyway.
 * 2. **The time is spent decoding art.** `preloadArt()` pulls the seven sprite sheets
 *    and backgrounds (~275 KB) into the image cache while the cards play. Without
 *    this the first frame of the first cave is a pop-in as `wall-tile.webp`,
 *    `rocks.webp` and the rest arrive one at a time. So the splash is not a tax on
 *    the first descent — it is the reason the first descent is not the slow one.
 *
 * The ShweTap card also ends *early* the moment boot is ready. Only the studio card
 * is a fixed beat, because a studio credit that disappears before it is read is not
 * a credit.
 *
 * And it is skippable. This is a game people open several times a day; branding that
 * cannot be tapped past stops being branding and becomes a toll.
 *
 * ## Everything is inline
 *
 * Same two constraints as `MiningLoader`, and for the same reason — this renders
 * before anything else, so it cannot depend on anything else:
 *
 * - **No image requests.** Both marks are inline SVG. A logo that has to download
 *   before the splash can paint is a splash screen for the splash screen.
 * - **Keyframes are co-located.** App CSS may not have landed yet, and a boot screen
 *   that needs a stylesheet to animate is not a boot screen.
 */

/** The studio credit. Fixed — it is shown in full whether boot is ready or not. */
export const STUDIO_MS = 820;
/** The ShweTap card's floor and ceiling. It ends at `ready`, clamped to this band. */
export const GAME_MIN_MS = 520;
export const GAME_MAX_MS = 1200;
/** Cross-fade between cards. */
export const FADE_MS = 260;

/** How long the whole sequence can hold the game up, worst case. */
export function maxTotalMs(): number {
  return STUDIO_MS + GAME_MAX_MS;
}

/**
 * When the ShweTap card should hand over, given how boot is going.
 *
 * Split out because it is the only decision in here with a wrong answer: too eager
 * and the card flashes, too patient and a ready app sits behind a logo.
 */
export function gameCardMs(ready: boolean): number {
  return ready ? GAME_MIN_MS : GAME_MAX_MS;
}

/**
 * Art the first minute of play needs, warmed while the cards are on screen.
 *
 * Deliberately fire-and-forget and deliberately unordered: nothing here blocks the
 * hand-over, because a player on a bad connection must not be held at a logo waiting
 * for a wall texture. If they arrive late the game degrades exactly as it does today.
 */
export function preloadArt(): void {
  const art = [
    '/wall-tile.webp',      // every frame of every cave
    '/rocks.webp',          // every rock in the corridor
    '/items.webp',          // every loot card
    '/cave-entrance.webp',  // the first few metres of a run
    '/cave-mouth-out.webp', // the last few
    '/gear.webp',
    '/heroes.webp',
  ];
  for (const src of art) {
    const img = new Image();
    /* `async` so decoding cannot compete with the splash's own paint on a slow
       device — the point is to be ready later, not to be busy now. */
    img.decoding = 'async';
    img.src = src;
  }
}

export function BootSplash({ ready, onDone }: {
  /** Boot has finished (or failed) and the app underneath can take over. */
  ready: boolean;
  onDone: () => void;
}) {
  const [card, setCard] = useState<'studio' | 'game'>('studio');
  const [leaving, setLeaving] = useState(false);
  const done = useRef(false);
  /* Held in a ref: an inline arrow at the call site changes identity every render,
     and as a dependency it would re-arm these timers forever. */
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const finish = useRef(() => {
    if (done.current) return;
    done.current = true;
    onDoneRef.current();
  }).current;

  useEffect(preloadArt, []);

  /* Studio → ShweTap. One effect per transition, because a single effect holding
     both timers gets its cleanup run by its own state change and cancels the second
     one — the bug that left `CaveExitCinematic` stuck on a white frame. */
  useEffect(() => {
    const t = window.setTimeout(() => setCard('game'), STUDIO_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (card !== 'game') return;
    const t = window.setTimeout(() => {
      setLeaving(true);
      window.setTimeout(finish, FADE_MS);
    }, gameCardMs(ready));
    return () => window.clearTimeout(t);
  }, [card, ready, finish]);

  return (
    <button
      type="button"
      onClick={finish}
      aria-label="Skip"
      className="absolute inset-0 z-50 block w-full cursor-default overflow-hidden
                 bg-[#07090E] text-left"
      /* Named so the sequence can be observed from outside. Both cards are in the
         DOM at once (they cross-fade), so "which card is up" is not something a
         screenshot or a test can otherwise ask. */
      data-splash={leaving ? 'leaving' : card}
      style={{ opacity: leaving ? 0 : 1, transition: `opacity ${FADE_MS}ms ease-in` }}
    >
      <style>{`
        @keyframes swtSplashIn  { from { opacity: 0; transform: scale(.94) }
                                  to   { opacity: 1; transform: scale(1) } }
        @keyframes swtSplashOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes swtPetal     { 0%,100% { transform: rotate(0deg) }
                                  50%     { transform: rotate(4deg) } }
        @keyframes swtShine     { 0%,100% { opacity: .55 } 50% { opacity: 1 } }
        @keyframes swtDig       { 0%   { opacity: .25 }
                                  40%  { opacity: 1 }
                                  100% { opacity: .25 } }
        .swt-splash-card { position: absolute; inset: 0; display: grid;
                           place-items: center; padding: 2rem; }
        .swt-petals { transform-box: view-box; transform-origin: 50% 50%; }
        @media (prefers-reduced-motion: reduce) {
          .swt-splash-card *, .swt-splash-card { animation: none !important;
                                                 transform: none !important;
                                                 opacity: 1 !important; }
        }
      `}</style>

      {/* ── 1. the studio ── */}
      <div
        className="swt-splash-card"
        style={{
          animation: card === 'studio'
            ? `swtSplashIn ${FADE_MS}ms ease-out both`
            : `swtSplashOut ${FADE_MS}ms ease-in both`,
          pointerEvents: 'none',
        }}
        aria-hidden={card !== 'studio'}
      >
        <div className="text-center">
          <SakuraMark />
          <p className="mt-5 font-pixel text-[10px] tracking-[.22em] text-[#F6D9DE]">
            REIWASAKURA
          </p>
          <p className="mt-2 text-[10px] tracking-[.34em] text-[#8C7A80]">.TECH</p>
        </div>
      </div>

      {/* ── 2. the game ── */}
      <div
        className="swt-splash-card"
        style={{
          animation: card === 'game'
            ? `swtSplashIn ${FADE_MS}ms ease-out both`
            : 'none',
          opacity: card === 'game' ? 1 : 0,
          pointerEvents: 'none',
        }}
        aria-hidden={card !== 'game'}
      >
        <div className="text-center">
          <ShweTapMark />
          <h1 className="mt-5 font-pixel text-2xl text-[#F5C542]
                         [text-shadow:0_3px_0_#8A5A0E,0_0_28px_rgba(245,197,66,.42)]">
            SHWETAP
          </h1>
          {/* "DIGGING", one letter at a time — the only motion on this card, and the
              one word that says what the game is. */}
          <p className="mt-4 font-pixel text-[8px] tracking-[.30em] text-[#A08D79]">
            {'DIGGING'.split('').map((ch, i) => (
              <span
                key={i}
                style={{ animation: `swtDig 1.4s ease-in-out ${i * 0.09}s infinite` }}
              >
                {ch}
              </span>
            ))}
          </p>
        </div>
      </div>

      <span className="sr-only" role="status" aria-live="polite">
        {card === 'studio' ? 'ReiwaSakura Tech' : 'ShweTap is loading'}
      </span>
    </button>
  );
}

/**
 * The studio mark — a five-petal sakura.
 *
 * Drawn rather than fetched, for the reason in the module note. It is a *drawn*
 * mark, not a scan of an existing brand asset: if ReiwaSakura has an official logo,
 * drop it in `assets/logo/` and replace this component, keeping it inline (an SVG
 * pasted here, not an `<img src>`) so the splash still paints with zero requests.
 */
function SakuraMark() {
  /* One petal, rotated five times. Authoring five near-identical paths by hand is how
     a blossom ends up subtly lopsided. */
  const petal =
    'M50 50 C42 34, 40 22, 46 14 C48 10, 49 12, 50 15.5 '
    + 'C51 12, 52 10, 54 14 C60 22, 58 34, 50 50 Z';

  return (
    <svg
      viewBox="0 0 100 100"
      className="mx-auto h-24 w-24"
      role="img"
      aria-label="ReiwaSakura Tech"
      style={{ animation: 'swtPetal 4s ease-in-out infinite' }}
    >
      <defs>
        <radialGradient id="swtPetalFill" cx="50%" cy="88%" r="82%">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="0.42" stopColor="#FFD9E2" />
          <stop offset="1" stopColor="#F2879F" />
        </radialGradient>
      </defs>
      {/* The bloom's own soft light. Keeps the mark off a flat black ground without
          adding a second colour — the same "draw it with light" rule the cave uses. */}
      <circle cx="50" cy="50" r="40" fill="#F2879F" opacity="0.10" />
      <g className="swt-petals">
        {[0, 72, 144, 216, 288].map((deg) => (
          <path
            key={deg}
            d={petal}
            fill="url(#swtPetalFill)"
            transform={`rotate(${deg} 50 50)`}
          />
        ))}
      </g>
      <circle cx="50" cy="50" r="5.4" fill="#F5C542" />
      <circle
        cx="50" cy="50" r="9.5"
        fill="none" stroke="#F5C542" strokeWidth="1.1" opacity="0.5"
        style={{ animation: 'swtShine 2.6s ease-in-out infinite' }}
      />
    </svg>
  );
}

/**
 * The ShweTap mark — the app icon, redrawn as inline SVG.
 *
 * A simplification of `assets/logo/shwetap-icon.svg`: same identity (dark rounded
 * square, teal halo, gold coin, cream tap wave, teal spark), fewer filters, and no
 * request. The PNG at `frontend/public/icon-512.png` is 294 KB — using it here would
 * mean the splash could not paint until a third of a megabyte had arrived.
 */
function ShweTapMark() {
  return (
    <svg
      viewBox="0 0 1024 1024"
      className="mx-auto h-24 w-24"
      role="img"
      aria-label="ShweTap"
    >
      <defs>
        <radialGradient id="swtCoin" cx="35%" cy="26%" r="74%">
          <stop offset="0" stopColor="#fff3a8" />
          <stop offset="0.38" stopColor="#ffc947" />
          <stop offset="0.74" stopColor="#ef8f19" />
          <stop offset="1" stopColor="#7b3d08" />
        </radialGradient>
        <linearGradient id="swtTap" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.45" stopColor="#ffe8a3" />
          <stop offset="1" stopColor="#ffae2e" />
        </linearGradient>
      </defs>
      <circle cx="512" cy="512" r="380" fill="#19c6b5" opacity="0.16" />
      <circle cx="512" cy="512" r="348" fill="url(#swtCoin)" />
      <circle cx="512" cy="512" r="264.6" fill="none" stroke="#fff3ae"
              strokeWidth="18" opacity="0.78" />
      <path
        d="M 358.8 574.7 C 484.1 358.8, 651.3 344.9, 630.4 505 C 612.9 637.3, 470.2 658.2, 358.8 574.7 Z"
        fill="#351b06" opacity="0.25"
      />
      <path
        d="M 390.1 539.9 C 498.1 372.7, 637.3 379.7, 616.4 498.1 C 595.6 595.6, 477.2 616.4, 390.1 539.9 Z"
        fill="url(#swtTap)"
      />
      <circle cx="560.7" cy="470.2" r="62.7" fill="#fff8d5" opacity="0.95" />
      <path
        d="M 686.1 303.1 L 727.9 404.1 L 828.8 435.4 L 734.8 484.1 L 717.4 588.6 L 654.7 501.6 L 546.8 494.6 L 644.3 445.8 Z"
        fill="#19c6b5" opacity="0.96"
        style={{ animation: 'swtShine 2.2s ease-in-out infinite' }}
      />
    </svg>
  );
}
